import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Trophy, Crown, Medal, Sparkles } from "lucide-react";

const CITIES = ["Bangalore", "Mumbai", "Delhi", "Hyderabad", "Pune", "Goa"];

type Row = {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  city: string;
  total_krews_attended: number;
  reliability_score: number;
  rank: number;
};

const rankIcon = (rank: number) => {
  if (rank === 1) return <Crown className="h-5 w-5 text-secondary" />;
  if (rank === 2) return <Medal className="h-5 w-5 text-muted-foreground" />;
  if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
  return <span className="w-5 text-center text-xs font-bold text-muted-foreground">{rank}</span>;
};

const Leaderboard = () => {
  const { profile } = useAuth();
  const [city, setCity] = useState<string>(profile?.city || "Bangalore");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    supabase.rpc("city_leaderboard", { p_city: city, p_limit: 25 }).then(({ data }) => {
      if (!active) return;
      setRows((data as Row[]) || []);
      setLoading(false);
    });
    return () => { active = false; };
  }, [city]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            <span className="text-gradient-neon">City</span> Leaderboard
          </h1>
          <p className="mt-1 text-sm text-muted-foreground font-body">
            The most reliable Krew faces in {city}.
          </p>
        </div>
        <Trophy className="h-10 w-10 text-secondary" />
      </div>

      <div className="flex flex-wrap gap-2">
        {CITIES.map((c) => (
          <button
            key={c}
            onClick={() => setCity(c)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
              city === c
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl bg-card" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center">
          <Sparkles className="mx-auto mb-3 h-8 w-8 text-primary" />
          <h3 className="font-semibold text-foreground">No nights logged yet in {city}</h3>
          <p className="mt-1 text-sm text-muted-foreground">Be the first on the board — attend a Krew.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {rows.map((row, i) => {
            const isMe = row.user_id === profile?.user_id;
            return (
              <motion.div
                key={row.user_id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
                className={`flex items-center gap-4 border-b border-border px-4 py-3 last:border-0 ${
                  isMe ? "bg-primary/5" : ""
                }`}
              >
                <div className="flex h-8 w-8 items-center justify-center">{rankIcon(Number(row.rank))}</div>
                {row.avatar_url ? (
                  <img src={row.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 font-bold text-primary">
                    {row.display_name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {row.display_name} {isMe && <span className="text-xs text-primary">(you)</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">{row.total_krews_attended} nights out</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-secondary">{Math.round(Number(row.reliability_score))}%</p>
                  <p className="text-[10px] text-muted-foreground">reliability</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;